import type { GetCurrentMemberFn } from '@/features/iam'
import { CursorPaged } from '@/infra/paging'
import { type Id } from '@/infra/id'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { type JobStore } from '@/features/jobs/JobStore'
import { type JobScopeChangeStore } from '@/features/jobs/job-scope-changes/JobScopeChangeStore'
import { type Job, type Resolution } from '@/features/jobs/Job'
import { computed, observable, action } from 'mobx'
import { formatMoney } from '@/utils/money-util'
import type {
  BillingMethodsFn,
  FetchBillingProfileFn,
} from '@/features/billing'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { task } from 'mobx-task'
import { distinctUntilChanged, filter, tap } from 'rxjs/operators'
import { whileActive } from '@/utils/rx-util'
import { tapAction, toStream } from '@/utils/mobx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * A resolution type.
 */
export type ViewResolution = 'Done' | 'Cancelled' | 'Test'

/**
 * A presentation-representation of a Job billing method.
 */
export interface ViewJobBillingMethod {
  /**
   * ID for this billing method, or `'default'`.
   */
  id: Id

  /**
   * Display friendly name for this billing method.
   */
  displayName: string
}

/**
 * Static `JobBillingMethod` operations.
 *
 * NOTICE: This is a view representation of a Job billing method.
 */
export const JobBillingMethod = {
  /**
   * Creates a default `ViewJobBillingMethod`.
   */
  get default(): ViewJobBillingMethod {
    return {
      id: 'default',
      displayName: 'Default',
    }
  },
}

/**
 * View Model for closing a job as an admin.
 */
export class CloseJobAsAdminDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private _loadedBillingMethods: BillingMethod[] = []

  readonly modal = new ModalViewModel()

  @observable
  accessor resolution: Resolution = 'Done'

  @observable
  accessor reason = ''

  @observable
  accessor shouldBillJob = true

  @observable
  accessor selectedBillingMethod: ViewJobBillingMethod =
    JobBillingMethod.default

  constructor(
    private readonly _job: Job,
    private readonly _jobStore: JobStore,
    private readonly _jobScopeChangeStore: JobScopeChangeStore,
    private readonly _fetchBillingProfile: FetchBillingProfileFn,
    private readonly _billingMethods: BillingMethodsFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {
    this._lifecycle.manage(
      this._billingMethods(this._job.ownerTeam ?? this._job.owner).pipe(
        tap((methods) => {
          this._loadedBillingMethods = methods
        }),
      ),
    )

    this._lifecycle.manage(
      toStream(() => this._job.status, true).pipe(
        distinctUntilChanged(),
        filter((s) => s === 'Closed'),
        whileActive(this.modal.showing$),
        tap(this.modal.close),
      ),
    )

    this._lifecycle.manage(
      toStream(() => this._resolvable, true).pipe(
        tapAction((isResolvable) => {
          this.resolution = isResolvable ? 'Done' : 'Cancelled'
        }),
      ),
    )
  }

  /**
   * Ways in which the job's resolution can be specified based on its status.
   */
  @computed
  get resolutionItems(): Resolution[] {
    return [
      ...(this._resolvable ? (['Done'] as const) : []),
      'Cancelled',
      'Test',
    ]
  }

  /**
   * Resolution options for the select, as `[value, label]` pairs.
   */
  @computed
  get resolutionSelectItems(): [Resolution, ViewResolution][] {
    return this.resolutionItems.map((resolution) => [
      resolution,
      resolutionLabel(resolution),
    ])
  }

  /**
   * Whether the job has a pending scope change request, which blocks closing.
   */
  @computed
  get isScopeChangeRequestPending() {
    return this._jobScopeChangeStore
      .forJob(this._job)
      .some((request) => request.isPending)
  }

  /**
   * Indicates whether the Administrator should be able to bill this job.
   */
  @computed
  get billingAllowed() {
    return (
      this._job.isBillingEnabled && this._job.balanceStatus !== 'BalanceZero'
    )
  }

  /**
   * Indicates whether we should collect the billing method to charge from
   * the Admin.
   */
  @computed
  get shouldCollectBillingMethod() {
    return this.billingAllowed && this.shouldBillJob
  }

  /**
   * Indicates whether the Admin has the ability to charge or refund the
   * payer based on the payment status/outstanding balance.
   */
  @computed
  get billingOperationMessage() {
    const owner = this._job.ownerTeam ? 'team' : 'client'
    const operationType =
      this._job.balanceStatus === 'BalanceNegative' ? 'Refund' : 'Bill'
    const prefix = `${operationType} the ${owner} the final amount`

    return `${prefix} (${formatMoney(Math.abs(this._job.balance))})`
  }

  /**
   * The name of the job.
   */
  @computed
  get jobName() {
    return this._job.name
  }

  /**
   * Closes the job with the reason and resolution specified by the Admin.
   */
  closeJob = task.resolved(async () => {
    switch (this.resolution) {
      case 'Done':
        await this._jobStore.complete({
          jobId: this._job.id,
          reason: this.reason,
          doBilling: this.shouldBillJob,
          billingMethodId: this.selectedBillingMethod.id,
        })
        break
      case 'Cancelled':
        await this._jobStore.cancel({
          jobId: this._job.id,
          reason: this.reason,
          doBilling: this.shouldBillJob,
          billingMethodId: this.selectedBillingMethod.id,
        })
        break
      case 'Test':
        await this._jobStore.closeJobAsTest({
          jobId: this._job.id,
          reason: this.reason,
          doBilling: this.shouldBillJob,
          billingMethodId: this.selectedBillingMethod.id,
        })
        break
    }

    this.modal.close()
  })

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches billing methods.
   */
  @action.bound
  async fetchBillingMethods() {
    if (!this._getCurrentMember().permissions.adminBillingManage) {
      return CursorPaged.empty<ViewJobBillingMethod>()
    }

    const billingMethods = (await this._fetchBillingMethods())
      .map(formatBillingMethod)
      .concat(JobBillingMethod.default)

    return CursorPaged.of(billingMethods)

    /**
     * Formats a billing method into its ID, owner, and display name.
     */
    function formatBillingMethod(method: BillingMethod): ViewJobBillingMethod {
      return {
        id: method.id,
        displayName:
          method.type.type === 'Credit'
            ? 'Team Credit'
            : `${method.stripeCard?.brand ?? ''} ending in ${method.stripeCard?.last4 ?? ''}`,
      }
    }
  }

  /**
   * Sets the billing method. Defaults to `JobBillingMethod.default` if not provided.
   */
  @action.bound
  setBillingMethod(billingMethod: ViewJobBillingMethod | null | undefined) {
    this.selectedBillingMethod = billingMethod ?? JobBillingMethod.default
  }

  private async _fetchBillingMethods(): Promise<BillingMethod[]> {
    // Without job billing nothing is fetched, but the methods already loaded
    // for the owner are still offered.
    if (!this._job.isBillingEnabled) {
      return this._loadedBillingMethods.slice()
    }

    const teamProfile = this._job.ownerTeam?.id
      ? await this._fetchBillingProfile(this._job.ownerTeam)
      : null
    const ownerProfile = await this._fetchBillingProfile(this._job.owner)
    const profile = this._job.ownerTeam ? teamProfile : ownerProfile

    return profile?.billingMethods.slice() ?? []
  }

  /**
   * Indicates whether the job is in a state where it might be possible that
   * it's done/complete.
   */
  @computed
  private get _resolvable() {
    return (
      this._job.status === 'Claimed' ||
      this._job.status === 'OnHold' ||
      this._job.status === 'Idle' ||
      this._job.status === 'InfoGathering'
    )
  }
}

function resolutionLabel(resolution: Resolution): ViewResolution {
  switch (resolution) {
    case 'Done':
      return 'Done'
    case 'Cancelled':
      return 'Cancelled'
    case 'Test':
      return 'Test'
  }
}
